import { useCallback, useState } from 'react'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DeleteForeverModal from 'modules/documents/components/TrashBin/TrashBinDataTable/_bulk_actions/DeleteForeverModal'
import RestoreModal from 'modules/documents/components/TrashBin/TrashBinDataTable/_bulk_actions/RestoreModal'
import useRemoveFromTrashBinDocument from 'modules/documents/hooks/documents/useRemoveFromTrashBinDocument'
import useRestoreFromTrashBinDocument from 'modules/documents/hooks/documents/useRestoreFromTrashBinDocument'
import { canDeleteDocument } from 'modules/documents/lib/Documents'

import type { TrashBinDocumentNode } from './types'

const ACTION_IDS = {
  RESTORE: 'restore',
  DELETE_FOREVER: 'delete_forever',
} as const

type ActionId = (typeof ACTION_IDS)[keyof typeof ACTION_IDS]

export function useItemActions() {
  const [restoreDoc, setRestoreDoc] = useState<TrashBinDocumentNode | null>(null)
  const [deleteDoc, setDeleteDoc] = useState<TrashBinDocumentNode | null>(null)

  const { restoreDocumentsFromTrashBin, loading: loadingRestore } = useRestoreFromTrashBinDocument({
    fromDataTable: true,
  })
  const { removeDocumentsFromTrashBin, loading: loadingRemove } = useRemoveFromTrashBinDocument({
    fromDataTable: true,
  })

  const getItemActions = useCallback((document: TrashBinDocumentNode) => {
    const canDelete = canDeleteDocument(document)

    const actions: Array<{
      id: ActionId
      label: string
      onClick: () => void
      critical?: boolean
    }> = []

    if (canDelete) {
      actions.push({
        id: ACTION_IDS.RESTORE,
        label: i18n.t('documents.trash_bin.restore_file'),
        onClick: () => setRestoreDoc(document),
      })

      actions.push({
        id: ACTION_IDS.DELETE_FOREVER,
        label: i18n.t('documents.trash_bin.delete'),
        onClick: () => setDeleteDoc(document),
        critical: true,
      })
    }

    return actions
  }, [])

  const onSubmitRestore = useCallback(async () => {
    if (!restoreDoc) return
    const result = await restoreDocumentsFromTrashBin({ documentIds: [restoreDoc.id] })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.trash_bin.restore.success', { count: 1 }),
      })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
    setRestoreDoc(null)
  }, [restoreDoc, restoreDocumentsFromTrashBin])

  const onSubmitDeleteForever = useCallback(async () => {
    if (!deleteDoc) return
    const result = await removeDocumentsFromTrashBin({ documentIds: [deleteDoc.id] })
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.tp('documents.trash_bin.delete_forever.success', { count: 1 }),
      })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
    setDeleteDoc(null)
  }, [deleteDoc, removeDocumentsFromTrashBin])

  const ItemActionModals = useCallback(
    () => (
      <>
        {restoreDoc && (
          <RestoreModal
            useF0
            filename={restoreDoc.filename}
            totalDocuments={1}
            loading={loadingRestore}
            onSubmit={onSubmitRestore}
            onClose={() => setRestoreDoc(null)}
          />
        )}
        {deleteDoc && (
          <DeleteForeverModal
            useF0
            filename={deleteDoc.filename}
            totalDocuments={1}
            onClose={() => setDeleteDoc(null)}
            onSubmit={onSubmitDeleteForever}
            loading={loadingRemove}
          />
        )}
      </>
    ),
    [restoreDoc, deleteDoc, onSubmitRestore, onSubmitDeleteForever, loadingRestore, loadingRemove]
  )

  return {
    itemActions: getItemActions,
    ItemActionModals,
  }
}
